import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import ResponsiveLayout from '../components/ResponsiveLayout';
import { AlertTriangle, ArrowLeft, Award, Baby, BarChart3, Brain, Calendar, CheckCircle, ClipboardList, Clock, Eye, FileText, Heart, Home, Mail, MapPin, Phone, Play, School, Shield, Smile, Star, Target, TrendingUp, User, UserCheck, UserPlus, Users, X, Zap } from 'lucide-react';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalChildDetail = () => {
  const navigate = useNavigate();
  const { childId } = useParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [child, setChild] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [insights, setInsights] = useState([]);
  const [profile, setProfile] = useState(null);
  const [showObserverModal, setShowObserverModal] = useState(false);
  const [availableObservers, setAvailableObservers] = useState([]);
  const [loadingObservers, setLoadingObservers] = useState(false);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadData();
  }, [childId, navigate]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('principal_token');
      
      const [childRes, sessionsRes, profileRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/principal/child/${childId}`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/principal/child/${childId}/sessions`, { params: { token, limit: 20 } }).catch(() => ({ data: { sessions: [] } })),
        axios.get(`${BACKEND_URL}/api/principal/child/${childId}/profile`, { params: { token } }).catch(() => ({ data: {} }))
      ]);
      
      setChild(childRes.data);
      setSessions(sessionsRes.data.sessions || []);
      setProfile(profileRes.data);
    } catch (error) {
      console.error('Error loading child data:', error);
      alert('Failed to load child details');
      navigate('/principal/child-assignment');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableObservers = async () => {
    setLoadingObservers(true);
    try {
      const token = localStorage.getItem('principal_token');
      const res = await axios.get(`${BACKEND_URL}/api/principal/observers`, { params: { token } });
      setAvailableObservers(res.data.observers || []);
    } catch (error) {
      console.error('Error loading observers:', error);
    } finally {
      setLoadingObservers(false);
    }
  };

  const addObserverToChild = async (observerId, isPrimary = false) => {
    setAssigning(true);
    try {
      const token = localStorage.getItem('principal_token');
      await axios.post(`${BACKEND_URL}/api/principal/child/${childId}/observers`, null, {
        params: { token, observer_id: observerId, is_primary: isPrimary }
      });
      
      toast({ 
        title: "Success", 
        description: isPrimary ? "Primary observer updated" : "Additional observer added" 
      });
      setShowObserverModal(false);
      loadData();
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || 'Failed to add observer',
        variant: "destructive"
      });
    } finally {
      setAssigning(false);
    }
  };

  const removeObserver = async (observerId) => {
    if (!confirm('Remove this observer from the child?')) return;
    
    try {
      const token = localStorage.getItem('principal_token');
      await axios.delete(`${BACKEND_URL}/api/principal/child/${childId}/observers/${observerId}`, {
        params: { token }
      });
      
      toast({ title: "Success", description: "Observer removed" });
      loadData();
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || 'Failed to remove observer',
        variant: "destructive"
      });
    }
  };

  const openObserverModal = () => {
    loadAvailableObservers();
    setShowObserverModal(true);
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-100';
    if (score >= 6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreLabel = (score) => {
    if (score >= 8) return 'Excellent';
    if (score >= 6) return 'Good';
    if (score >= 4) return 'Fair';
    return 'Needs Attention';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sanjaya-navy"></div>
        </div>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="min-h-screen bg-slate-50">
        
        <div className="pt-20 text-center">
          <p className="text-slate-500">Child not found</p>
          <Button onClick={() => navigate('/principal/child-assignment')} className="mt-4">
            Back to Children
          </Button>
        </div>
      </div>
    );
  }

  const scores = profile?.latest_scores || child.scores || {};
  const avgScore = Object.values(scores).length > 0 
    ? (Object.values(scores).reduce((a, b) => a + b, 0) / Object.values(scores).length).toFixed(1)
    : 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="child-detail-page">
      
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          {/* Header Card */}
          <Card className="mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6 text-white">
              <div className="flex items-center gap-6">
                <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center text-4xl font-bold">
                  {child.name?.charAt(0)}
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl font-bold">{child.name}</h1>
                  <div className="flex items-center gap-4 mt-2 text-blue-100">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Age: {child.age || 'N/A'}
                    </span>
                    <span className="flex items-center gap-1">
                      <School className="w-4 h-4" />
                      {child.class || 'N/A'}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {child.gender || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className="text-center">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold ${getScoreColor(avgScore)}`}>
                    {avgScore}
                  </div>
                  <p className="text-sm mt-1 text-blue-100">Overall Score</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Observer Assignment Section */}
          <Card className="mb-6" data-testid="observer-assignment-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  Assigned Observers
                </CardTitle>
                <Button size="sm" onClick={openObserverModal} data-testid="add-observer-btn">
                  <UserPlus className="w-4 h-4 mr-2" /> Add Observer
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Primary Observer */}
                {child.observer_id ? (
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold">
                        {child.observer_name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{child.observer_name}</p>
                        <span className="text-xs px-2 py-0.5 bg-purple-200 text-purple-700 rounded-full">Primary</span>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeObserver(child.observer_id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 text-center">
                    <AlertTriangle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                    <p className="text-yellow-700 font-medium">No Primary Observer Assigned</p>
                    <p className="text-sm text-yellow-600">Click &ldquo;Add Observer&rdquo; to assign one</p>
                  </div>
                )}
                
                {/* Additional Observers */}
                {child.additional_observers?.length > 0 && (
                  <>
                    <p className="text-sm font-medium text-slate-500 mt-4">Additional Observers</p>
                    {child.additional_observers_details?.map((obs, idx) => (
                      <div key={obs.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-400 text-white rounded-full flex items-center justify-center font-bold">
                            {obs.name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{obs.name}</p>
                            <span className="text-xs px-2 py-0.5 bg-gray-200 text-slate-600 rounded-full">Additional</span>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeObserver(obs.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Details */}
            <div className="space-y-6">
              {/* Personal Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Baby className="w-5 h-5 text-sanjaya-navy" />
                    Personal Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Full Name</span>
                    <span className="font-medium">{child.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Age</span>
                    <span className="font-medium">{child.age} years</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Gender</span>
                    <span className="font-medium">{child.gender || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Class</span>
                    <span className="font-medium">{child.class || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">School</span>
                    <span className="font-medium">{child.school_name || child.school || 'N/A'}</span>
                  </div>
                  {child.preferred_time && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Preferred Time</span>
                      <span className="font-medium">{child.preferred_time}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Parent Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-600" />
                    Parent Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {child.parent_name && (
                    <div>
                      <p className="text-sm text-slate-500">Primary Contact</p>
                      <p className="font-medium">{child.parent_name}</p>
                    </div>
                  )}
                  {child.parent_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{child.parent_phone}</span>
                    </div>
                  )}
                  {child.parent_email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span className="text-sm">{child.parent_email}</span>
                    </div>
                  )}
                  {child.monitor_at_home && (
                    <div>
                      <p className="text-sm text-slate-500">Monitor at Home</p>
                      <p className="font-medium">{child.monitor_at_home}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Qualities & Hobbies */}
              {(child.qualities || child.hobbies) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="w-5 h-5 text-pink-600" />
                      Qualities & Interests
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {child.qualities && (
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Qualities</p>
                        <p className="text-sm">{child.qualities}</p>
                      </div>
                    )}
                    {child.hobbies && (
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Hobbies</p>
                        <p className="text-sm">{child.hobbies}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Middle Column - Growth Scores */}
            <div className="space-y-6">
              {/* Growth Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                    Growth Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { key: 'emotional_awareness', label: 'Emotional Awareness', icon: Smile, color: 'text-pink-600' },
                      { key: 'self_expression', label: 'Self Expression', icon: Zap, color: 'text-yellow-600' },
                      { key: 'social_confidence', label: 'Social Confidence', icon: Users, color: 'text-sanjaya-navy' },
                      { key: 'curiosity_learning', label: 'Curiosity & Learning', icon: Brain, color: 'text-purple-600' },
                      { key: 'communication_clarity', label: 'Communication', icon: FileText, color: 'text-green-600' },
                      { key: 'overall_wellbeing', label: 'Overall Wellbeing', icon: Heart, color: 'text-red-600' }
                    ].map(metric => {
                      const score = scores[metric.key] || 0;
                      const Icon = metric.icon;
                      return (
                        <div key={metric.key} className="flex items-center gap-3">
                          <Icon className={`w-5 h-5 ${metric.color}`} />
                          <div className="flex-1">
                            <div className="flex justify-between mb-1">
                              <span className="text-sm font-medium">{metric.label}</span>
                              <span className={`text-sm font-bold ${score >= 7 ? 'text-green-600' : score >= 5 ? 'text-yellow-600' : 'text-red-600'}`}>
                                {score}/10
                              </span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${score >= 7 ? 'bg-green-500' : score >= 5 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${score * 10}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Session Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-indigo-600" />
                    Session Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-3xl font-bold text-sanjaya-navy">{profile?.total_calls || sessions.length || 0}</p>
                      <p className="text-sm text-slate-600">Total Sessions</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-3xl font-bold text-green-600">{avgScore}</p>
                      <p className="text-sm text-slate-600">Avg Score</p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <p className="text-3xl font-bold text-purple-600">{profile?.topics_discussed?.length || 0}</p>
                      <p className="text-sm text-slate-600">Topics Covered</p>
                    </div>
                    <div className="text-center p-4 bg-amber-50 rounded-lg">
                      <p className="text-3xl font-bold text-amber-600">{getScoreLabel(avgScore)}</p>
                      <p className="text-sm text-slate-600">Status</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Recent Sessions */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="w-5 h-5 text-rose-600" />
                    Recent Sessions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {sessions.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>No sessions recorded yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {sessions.slice(0, 10).map((session, idx) => (
                        <div 
                          key={session.id || idx}
                          className="p-3 bg-slate-50 rounded-lg border hover:bg-slate-100 cursor-pointer"
                          onClick={() => navigate(`/principal/recordings?session=${session.id}`)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{session.date || session.session_date}</p>
                              <p className="text-xs text-slate-500">{session.observer_name}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {session.overall_scores && (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  getScoreColor(Object.values(session.overall_scores).reduce((a,b) => a+b, 0) / Object.values(session.overall_scores).length)
                                }`}>
                                  {(Object.values(session.overall_scores).reduce((a,b) => a+b, 0) / Object.values(session.overall_scores).length).toFixed(1)}
                                </span>
                              )}
                              <Play className="w-4 h-4 text-slate-400" />
                            </div>
                          </div>
                          {session.session_summary && (
                            <p className="text-xs text-slate-600 mt-2 line-clamp-2">{session.session_summary}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Key Quotes */}
              {profile?.key_quotes?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Award className="w-5 h-5 text-amber-600" />
                      Notable Quotes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {profile.key_quotes.slice(0, 5).map((quote, idx) => (
                        <div key={idx} className="p-3 bg-amber-50 rounded-lg border-l-4 border-amber-400">
                          <p className="text-sm italic">&ldquo;{quote}&rdquo;</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Observer Modal */}
      {showObserverModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="add-observer-modal">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Add Observer for {child.name}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowObserverModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <p className="text-sm text-slate-500">
                {child.observer_id 
                  ? "Select an observer to add as additional support" 
                  : "Select an observer to assign as primary"}
              </p>
            </CardHeader>
            <CardContent>
              {loadingObservers ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="text-slate-500 mt-2">Loading observers...</p>
                </div>
              ) : availableObservers.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No observers available</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {availableObservers
                    .filter(obs => obs.id !== child.observer_id && !child.additional_observers?.includes(obs.id))
                    .map(obs => (
                      <div 
                        key={obs.id}
                        className="p-3 border rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-bold">
                              {obs.name?.charAt(0)}
                            </div>
                            <div>
                              <p className="font-medium">{obs.name}</p>
                              <p className="text-sm text-slate-500">{obs.email}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {!child.observer_id && (
                              <Button 
                                size="sm"
                                onClick={() => addObserverToChild(obs.id, true)}
                                disabled={assigning}
                                className="bg-purple-600 hover:bg-purple-700"
                              >
                                {assigning ? '...' : 'Primary'}
                              </Button>
                            )}
                            <Button 
                              size="sm"
                              variant={child.observer_id ? "default" : "outline"}
                              onClick={() => addObserverToChild(obs.id, false)}
                              disabled={assigning}
                              className={child.observer_id ? "bg-sanjaya-navy hover:bg-sanjaya-navy-dark" : ""}
                            >
                              {assigning ? '...' : child.observer_id ? 'Add' : 'Additional'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PrincipalChildDetail;
